import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FloppyDiskIcon as FloppyDisk, ArrowsSplitIcon as ArrowsSplit, CheckCircleIcon as CheckCircle, WarningCircleIcon as WarningCircle, LockIcon as Lock, ArrowsClockwiseIcon as ArrowsClockwise, ClockCounterClockwiseIcon as ClockCounterClockwise, ScalesIcon as Scales, ListMagnifyingGlassIcon as ListMagnifyingGlass } from "@phosphor-icons/react";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const nfEuro = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const eu = (v) => (v == null ? "—" : nfEuro.format(v));
const STATI = [
  { k: "in_maturazione", l: "In maturazione" }, { k: "maturato", l: "Maturato" },
  { k: "congelato", l: "Congelato" }, { k: "sbloccato_semestre", l: "Sbloccato a semestre" },
  { k: "sbloccato_fine_anno", l: "Sbloccato a fine anno" }, { k: "assegnato", l: "Assegnato" }, { k: "erogato", l: "Erogato" },
];
const statoLbl = (k) => (STATI.find((s) => s.k === k)?.l) || k;
const fmtDate = (s) => { try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };

export function SistemistiPremiSettings({ anno }) {
  const [cfg, setCfg] = useState(null);
  const [rie, setRie] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [trState, setTrState] = useState({});
  // versioning dialogs
  const [ricalc, setRicalc] = useState(null); // {trimestre}
  const [ricalcMot, setRicalcMot] = useState("");
  const [blocca, setBlocca] = useState(null); // {trimestre}
  const [bloccaMot, setBloccaMot] = useState("");
  const [storico, setStorico] = useState(null); // {trimestre, versioni}
  const [attiva, setAttiva] = useState(null); // {version}
  const [attivaMot, setAttivaMot] = useState("");
  const [confronto, setConfronto] = useState(null);

  const loadCfg = () => api.get(`/reportistica/sistemisti/premi/config?anno=${anno}`).then(({ data }) => setCfg(data)).catch(() => {});
  const loadRie = () => api.get(`/reportistica/sistemisti/premi/riepilogo?anno=${anno}`).then(({ data }) => {
    setRie(data); const st = {}; data.trimestri.forEach((t) => { st[t.trimestre] = { stato: t.stato, nota: t.nota || "" }; }); setTrState(st);
  }).catch(() => {});
  useEffect(() => { loadCfg(); loadRie(); /* eslint-disable-next-line */ }, [anno]);

  const sommaMensili = useMemo(() => cfg ? Object.values(cfg.budget_mensili).reduce((a, b) => a + (Number(b) || 0), 0) : 0, [cfg]);
  const diff = useMemo(() => cfg ? Math.round((sommaMensili - Number(cfg.budget_annuale || 0)) * 100) / 100 : 0, [cfg, sommaMensili]);
  const premiUsato = useMemo(() => {
    const used = (rie?.mesi || []).reduce((s, m) => s + (m.premio_assegnato || 0), 0);
    const budget = Number(cfg?.budget_annuale || 0);
    const perc = budget > 0 ? Math.round((used / budget) * 1000) / 10 : (used > 0 ? 100 : 0);
    return { used, budget, residuo: budget - used, perc, over: used > budget + 0.001 ? used - budget : 0 };
  }, [rie, cfg]);

  const setBm = (m, v) => setCfg((c) => ({ ...c, budget_mensili: { ...c.budget_mensili, [m]: v } }));
  const ripartisci = () => setCfg((c) => {
    const tot = Math.round(Number(c.budget_annuale || 0) * 100);
    const base = Math.floor(tot / 12), resto = tot - base * 12;
    const bm = {};
    for (let m = 1; m <= 12; m++) bm[String(m)] = ((base + (m <= resto ? 1 : 0)) / 100);
    return { ...c, budget_mensili: bm };
  });

  const saveCfg = async () => {
    if (Math.abs(diff) > 0.01) return toast.error("La somma dei budget mensili deve coincidere con il budget annuale");
    setSaving(true);
    try {
      const { data } = await api.put("/reportistica/sistemisti/premi/config", {
        anno, budget_annuale: Number(cfg.budget_annuale) || 0, cap_mensile_tecnico: Number(cfg.cap_mensile_tecnico) || 0,
        budget_mensili: Object.fromEntries(Object.entries(cfg.budget_mensili).map(([k, v]) => [k, Number(v) || 0])) });
      setCfg(data); toast.success("Configurazione premi salvata"); loadRie();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const saveTrimestre = async (tr) => {
    try {
      await api.put("/reportistica/sistemisti/premi/trimestre", { anno, trimestre: tr, stato: trState[tr].stato, nota: trState[tr].nota });
      toast.success(`Trimestre Q${tr} aggiornato`); loadRie();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const doBlocca = async () => {
    if (!bloccaMot.trim()) return toast.error("La motivazione è obbligatoria");
    try {
      await api.post("/reportistica/sistemisti/premi/snapshot", { anno, trimestre: blocca.trimestre, motivazione: bloccaMot.trim() });
      toast.success("Dati trimestre bloccati (versione attiva creata)"); setBlocca(null); setBloccaMot(""); loadRie();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doRicalc = async () => {
    if (!ricalcMot.trim()) return toast.error("La motivazione è obbligatoria");
    try {
      const { data } = await api.post("/reportistica/sistemisti/premi/versioni/ricalcola", { anno, trimestre: ricalc.trimestre, motivazione: ricalcMot.trim() });
      toast.success(`Versione ${data.versione} creata (bozza). Attivala dallo storico se confermata.`);
      const tr = ricalc.trimestre; setRicalc(null); setRicalcMot(""); loadRie();
      openStorico(tr);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openStorico = async (tr) => {
    try { const { data } = await api.get(`/reportistica/sistemisti/premi/versioni?anno=${anno}&trimestre=${tr}`); setStorico({ trimestre: tr, versioni: data.versioni }); }
    catch { toast.error("Errore caricamento storico"); }
  };
  const doAttiva = async () => {
    if (!attivaMot.trim()) return toast.error("La motivazione è obbligatoria");
    try {
      await api.post("/reportistica/sistemisti/premi/versioni/attiva", { id: attiva.id, motivazione: attivaMot.trim() });
      toast.success(`Versione ${attiva.versione} impostata come attiva`);
      const tr = storico?.trimestre; setAttiva(null); setAttivaMot(""); loadRie();
      if (tr) openStorico(tr);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openConfronto = async (tr, base_id, target_id) => {
    try {
      const q = new URLSearchParams({ anno, trimestre: tr });
      if (base_id) q.set("base_id", base_id); if (target_id) q.set("target_id", target_id);
      const { data } = await api.get(`/reportistica/sistemisti/premi/versioni/confronto?${q}`); setConfronto(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore confronto"); }
  };
  const openDetail = async (mese) => {
    try { const { data } = await api.get(`/reportistica/sistemisti/performance?anno=${anno}&mese=${mese}`); setDetail({ mese, rows: data.rows }); }
    catch { toast.error("Errore caricamento dettaglio"); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Caricamento…</div>;

  return (
    <div className="space-y-5" data-testid="sistemisti-premi-settings">
      {/* Utilizzo budget annuale premi */}
      <Card className="rounded-sm border-border shadow-none p-4 max-w-md" data-testid="premi-budget-box-settings">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Premi Performance · utilizzo budget annuale {anno}</p>
        <div className="flex items-end justify-between mt-1">
          <div><span className="text-lg font-semibold">{eu(premiUsato.used)}</span><span className="text-xs text-muted-foreground"> / {eu(premiUsato.budget)}</span></div>
          <span className={`text-sm font-medium ${premiUsato.over > 0 ? "text-rose-600" : "text-muted-foreground"}`}>{premiUsato.perc}%</span>
        </div>
        <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden"><div className={`h-full ${premiUsato.over > 0 ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${Math.min(100, premiUsato.perc)}%` }} /></div>
        {premiUsato.over > 0
          ? <p className="text-xs mt-1 text-rose-600 font-medium flex items-center gap-1"><WarningCircle size={12} weight="fill" /> Superamento: {eu(premiUsato.over)}</p>
          : <p className="text-xs mt-1 text-muted-foreground">Residuo: {eu(premiUsato.residuo)}</p>}
      </Card>

      {/* Config annuale premi */}
      <Card className="rounded-sm border-border shadow-none p-5" data-testid="premi-config">
        <h4 className="font-heading font-semibold mb-1">Configurazione premi {anno}</h4>
        <p className="text-xs text-muted-foreground mb-4">Budget premi di rendimento e cap mensile per tecnico (storicizzati per anno).</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div><Label className="text-xs uppercase tracking-wider">Budget premi annuale (€)</Label>
            <Input type="number" step="0.01" value={cfg.budget_annuale} onChange={(e) => setCfg((c) => ({ ...c, budget_annuale: e.target.value }))} className="mt-1 rounded-sm" data-testid="premi-annuale" /></div>
          <div><Label className="text-xs uppercase tracking-wider">Cap massimo mensile / tecnico (€)</Label>
            <Input type="number" step="0.01" value={cfg.cap_mensile_tecnico} onChange={(e) => setCfg((c) => ({ ...c, cap_mensile_tecnico: e.target.value }))} className="mt-1 rounded-sm" data-testid="premi-cap" /></div>
          <div className="flex items-end"><Button variant="outline" className="rounded-sm gap-1.5 w-full" onClick={ripartisci} data-testid="premi-ripartisci"><ArrowsSplit size={15} /> Ripartisci sui 12 mesi</Button></div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {MESI.map((m, i) => (
            <div key={i}><Label className="text-[11px] text-muted-foreground">{m}</Label>
              <Input type="number" step="0.01" value={cfg.budget_mensili[String(i + 1)]} onChange={(e) => setBm(String(i + 1), e.target.value)} className="mt-1 rounded-sm h-8 text-sm" data-testid={`premi-bm-${i + 1}`} /></div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-4 text-sm">
          <span>Totale mensili: <b className="tabular-nums">{eu(sommaMensili)}</b></span>
          {Math.abs(diff) <= 0.01
            ? <span className="text-green-700 flex items-center gap-1" data-testid="premi-sum-ok"><CheckCircle size={15} weight="fill" /> Coincide con il budget annuale</span>
            : <span className="text-red-700 flex items-center gap-1" data-testid="premi-sum-err"><WarningCircle size={15} weight="fill" /> Differenza da distribuire: {eu(diff)}</span>}
          <Button className="ml-auto rounded-sm gap-1.5" onClick={saveCfg} disabled={saving || Math.abs(diff) > 0.01} data-testid="premi-save"><FloppyDisk size={15} /> Salva</Button>
        </div>
      </Card>

      <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3" data-testid="versioni-info">
        Le <b>versioni bloccate</b> conservano i valori dei premi approvati. Eventuali modifiche successive a rapportini, giorni lavorati o configurazioni non modificano automaticamente i dati già bloccati.
      </div>

      {/* Trimestri */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="premi-trimestri">
        {(rie?.trimestri || []).map((t) => (
          <Card key={t.trimestre} className="rounded-sm border-border shadow-none p-4" data-testid={`trimestre-${t.trimestre}`}>
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-heading font-semibold">{t.trimestre}° trimestre <span className="text-xs text-muted-foreground font-normal">({MESI[t.mesi[0] - 1].slice(0, 3)}–{MESI[t.mesi[2] - 1].slice(0, 3)})</span></h5>
              {t.bloccato
                ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 flex items-center gap-1"><Lock size={11} /> Versione {t.versione_attiva ?? "—"} attiva</span>
                : <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">Non bloccato</span>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Budget</span><b>{eu(t.budget)}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Premi CAP</span><b>{eu(t.premio_cap)}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Assegnati</span><b>{eu(t.premio_assegnato)}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Residuo</span><b>{eu(t.residuo_congelato)}</b></div>
              <div className="flex justify-between col-span-2"><span className="text-muted-foreground">Versioni bloccate</span><b>{t.num_versioni || 0}</b></div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={trState[t.trimestre]?.stato} onValueChange={(v) => setTrState((s) => ({ ...s, [t.trimestre]: { ...s[t.trimestre], stato: v } }))}>
                <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`tr-stato-${t.trimestre}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => saveTrimestre(t.trimestre)} data-testid={`tr-save-${t.trimestre}`}>Aggiorna</Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {t.num_versioni === 0
                ? <Button size="sm" variant="ghost" className="rounded-sm h-7 gap-1 text-xs" onClick={() => { setBlocca({ trimestre: t.trimestre }); setBloccaMot(""); }} data-testid={`tr-blocca-${t.trimestre}`}><Lock size={13} /> Blocca dati trimestre</Button>
                : <Button size="sm" variant="ghost" className="rounded-sm h-7 gap-1 text-xs" onClick={() => { setRicalc({ trimestre: t.trimestre }); setRicalcMot(""); }} data-testid={`tr-ricalcola-${t.trimestre}`}><ArrowsClockwise size={13} /> Ricalcola nuova versione</Button>}
              {t.num_versioni > 0 && <Button size="sm" variant="ghost" className="rounded-sm h-7 gap-1 text-xs" onClick={() => openStorico(t.trimestre)} data-testid={`tr-storico-${t.trimestre}`}><ClockCounterClockwise size={13} /> Storico versioni</Button>}
              {t.num_versioni > 0 && <Button size="sm" variant="ghost" className="rounded-sm h-7 gap-1 text-xs" onClick={() => openConfronto(t.trimestre, t.versione_attiva_id, "")} data-testid={`tr-confronto-${t.trimestre}`}><Scales size={13} /> Confronta con correnti</Button>}
            </div>
            <Input placeholder="Nota amministrativa" value={trState[t.trimestre]?.nota || ""} onChange={(e) => setTrState((s) => ({ ...s, [t.trimestre]: { ...s[t.trimestre], nota: e.target.value } }))} className="mt-2 rounded-sm h-8 text-xs" data-testid={`tr-nota-${t.trimestre}`} />
            {t.updated_by && <p className="text-[10px] text-muted-foreground mt-1">Aggiornato da {t.updated_by}</p>}
          </Card>
        ))}
      </div>

      {/* Dettaglio mensile */}
      <Card className="rounded-sm border-border shadow-none overflow-hidden" data-testid="premi-mensile">
        <div className="p-4 border-b border-border"><h4 className="font-heading font-semibold">Dettaglio mensile</h4></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs whitespace-nowrap">
            <thead><tr className="text-left border-b border-border bg-secondary/40">
              {["Mese", "Budget mese", "Somma score", "Premio teorico", "Premio dopo CAP", "Premio assegnato", "Residuo", "Trimestre", "Stato", ""].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {(rie?.mesi || []).map((m) => (
                <tr key={m.mese} className="border-b border-border/40" data-testid={`mese-row-${m.mese}`}>
                  <td className="px-3 py-1.5 font-medium">{m.mese_nome}{m.snapshot && <Lock size={11} className="inline ml-1 text-blue-500" />}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eu(m.budget_mese)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{(m.somma_score || 0).toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eu(m.premio_teorico)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eu(m.premio_cap)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eu(m.premio_assegnato)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{eu(m.residuo_congelato)}</td>
                  <td className="px-3 py-1.5 text-center">Q{m.trimestre}</td>
                  <td className="px-3 py-1.5">{statoLbl(m.stato_trimestre)}</td>
                  <td className="px-3 py-1.5"><button className="text-primary hover:underline flex items-center gap-1" onClick={() => openDetail(m.mese)} data-testid={`mese-detail-${m.mese}`}><ListMagnifyingGlass size={13} /> Dettaglio tecnici</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog blocca dati */}
      <Dialog open={!!blocca} onOpenChange={(o) => !o && setBlocca(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="blocca-dialog">
          <DialogHeader><DialogTitle>Blocca dati trimestre Q{blocca?.trimestre}</DialogTitle>
            <DialogDescription>Crea la prima versione bloccata (attiva) con i valori correnti.</DialogDescription></DialogHeader>
          <div><Label className="text-xs uppercase tracking-wider">Motivazione <span className="text-rose-500">*</span></Label>
            <Textarea value={bloccaMot} onChange={(e) => setBloccaMot(e.target.value)} rows={2} className="mt-1 rounded-sm" data-testid="blocca-mot" /></div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setBlocca(null)}>Annulla</Button><Button className="rounded-sm" onClick={doBlocca} data-testid="blocca-save">Blocca</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ricalcola nuova versione */}
      <Dialog open={!!ricalc} onOpenChange={(o) => !o && setRicalc(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="ricalcola-dialog">
          <DialogHeader><DialogTitle>Ricalcola nuova versione Q{ricalc?.trimestre}</DialogTitle>
            <DialogDescription>Crea una nuova versione (bozza) dai dati correnti. La versione attiva NON viene modificata.</DialogDescription></DialogHeader>
          <div><Label className="text-xs uppercase tracking-wider">Motivazione <span className="text-rose-500">*</span></Label>
            <Textarea value={ricalcMot} onChange={(e) => setRicalcMot(e.target.value)} rows={2} className="mt-1 rounded-sm" data-testid="ricalcola-mot" /></div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setRicalc(null)}>Annulla</Button><Button className="rounded-sm" onClick={doRicalc} data-testid="ricalcola-save">Crea versione</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog attiva versione */}
      <Dialog open={!!attiva} onOpenChange={(o) => !o && setAttiva(null)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="attiva-dialog">
          <DialogHeader><DialogTitle>Imposta Versione {attiva?.versione} come attiva</DialogTitle>
            <DialogDescription>La versione attualmente attiva verrà archiviata. Operazione registrata nell'audit.</DialogDescription></DialogHeader>
          <div><Label className="text-xs uppercase tracking-wider">Motivazione <span className="text-rose-500">*</span></Label>
            <Textarea value={attivaMot} onChange={(e) => setAttivaMot(e.target.value)} rows={2} className="mt-1 rounded-sm" data-testid="attiva-mot" /></div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setAttiva(null)}>Annulla</Button><Button className="rounded-sm" onClick={doAttiva} data-testid="attiva-save">Imposta attiva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog storico versioni */}
      <Dialog open={!!storico} onOpenChange={(o) => !o && setStorico(null)}>
        <DialogContent className="rounded-sm max-w-3xl" data-testid="storico-dialog">
          <DialogHeader><DialogTitle>Storico versioni — Q{storico?.trimestre} {anno}</DialogTitle>
            <DialogDescription>Ogni versione è immutabile. Una sola versione può essere attiva.</DialogDescription></DialogHeader>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-xs whitespace-nowrap">
              <thead><tr className="text-left border-b border-border bg-secondary/40">
                {["Versione", "Stato", "Creata da", "Data", "Motivazione", "Azioni"].map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {(storico?.versioni || []).map((v) => (
                  <tr key={v.id} className="border-b border-border/40" data-testid={`ver-row-${v.versione}`}>
                    <td className="px-2 py-1.5 font-medium">Versione {v.versione}</td>
                    <td className="px-2 py-1.5">{v.attiva ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-1 w-fit"><CheckCircle size={11} weight="fill" /> Attiva</span> : <span className="text-muted-foreground">{v.stato}</span>}</td>
                    <td className="px-2 py-1.5">{v.created_by || "—"}</td>
                    <td className="px-2 py-1.5">{fmtDate(v.created_at)}</td>
                    <td className="px-2 py-1.5 text-muted-foreground max-w-[200px] truncate" title={v.motivazione}>{v.motivazione || "—"}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <button className="text-primary hover:underline" onClick={() => openConfronto(storico.trimestre, storico.versioni.find((x) => x.attiva)?.id || "", v.id)} data-testid={`ver-confronta-${v.versione}`}>Confronta con attiva</button>
                        {!v.attiva && <button className="text-primary hover:underline" onClick={() => { setAttiva({ id: v.id, versione: v.versione }); setAttivaMot(""); }} data-testid={`ver-attiva-${v.versione}`}>Imposta attiva</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setStorico(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confronto versioni */}
      <Dialog open={!!confronto} onOpenChange={(o) => !o && setConfronto(null)}>
        <DialogContent className="rounded-sm max-w-5xl" data-testid="premi-confronto-dialog">
          <DialogHeader><DialogTitle>Confronto — Q{confronto?.trimestre} {confronto?.anno}</DialogTitle>
            <DialogDescription>{confronto?.base?.label} vs {confronto?.target?.label}</DialogDescription></DialogHeader>
          {confronto && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {[["Budget", "budget"], ["Premi CAP", "cap"], ["Assegnati", "assegnato"], ["Residuo", "residuo"]].map(([lbl, k]) => (
                  <div key={k} className="bg-secondary/40 rounded-sm px-3 py-2">
                    <div className="text-[11px] text-muted-foreground">{lbl}</div>
                    <div className="flex items-center justify-between"><span>{confronto.base.label}</span><b>{eu(confronto.riepilogo[`${k}_base`])}</b></div>
                    <div className="flex items-center justify-between"><span>{confronto.target.label}</span><b>{eu(confronto.riepilogo[`${k}_target`])}</b></div>
                    {["cap", "assegnato", "budget"].includes(k) && (
                      <div className={`text-xs mt-1 font-semibold ${confronto.riepilogo[`diff_${k}`] > 0 ? "text-emerald-600" : confronto.riepilogo[`diff_${k}`] < 0 ? "text-rose-600" : "text-muted-foreground"}`}>
                        {confronto.riepilogo[`diff_${k}`] > 0 ? "+" : ""}{eu(confronto.riepilogo[`diff_${k}`])}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border border-border rounded-sm">
                <table className="w-full text-xs whitespace-nowrap">
                  <thead><tr className="text-left border-b border-border bg-secondary/40">
                    {["Mese", "Tecnico", "GG", "CAP (base→nuovo)", "Δ CAP", "Assegnato (base→nuovo)", "Δ Assegnato"].map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {confronto.righe.map((r, i) => (
                      <tr key={i} className={`border-b border-border/40 ${!r.presente_base ? "bg-emerald-50" : !r.presente_target ? "bg-rose-50" : ""}`}>
                        <td className="px-2 py-1.5">{r.mese_nome}</td>
                        <td className="px-2 py-1.5 font-medium">{r.tecnico}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.giorni_base ?? "—"} → {r.giorni_target ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{eu(r.cap_base)} → {eu(r.cap_target)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.diff_cap > 0 ? "text-emerald-600" : r.diff_cap < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{r.diff_cap > 0 ? "+" : ""}{eu(r.diff_cap)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{eu(r.assegnato_base)} → {eu(r.assegnato_target)}</td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.diff_assegnato > 0 ? "text-emerald-600" : r.diff_assegnato < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{r.diff_assegnato > 0 ? "+" : ""}{eu(r.diff_assegnato)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setConfronto(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dettaglio mensile tecnici */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-4xl" data-testid="mese-detail-dialog">
          <DialogHeader><DialogTitle>Dettaglio tecnici — {detail && MESI[detail.mese - 1]} {anno}</DialogTitle></DialogHeader>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-xs whitespace-nowrap">
              <thead><tr className="text-left border-b border-border bg-secondary/40">
                {["Tecnico", "ULA", "GG lav.", "GG teorici", "Rend.%", "Score", "Premio calc.", "Premio CAP", "Assegnato", "Override", "Motivazione"].map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {(detail?.rows || []).map((r) => (
                  <tr key={r.tecnico_id} className="border-b border-border/40">
                    <td className="px-2 py-1.5 font-medium">{r.tecnico}</td>
                    <td className="px-2 py-1.5 text-right">{(r.peso_ula ?? 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right">{r.giorni_lavorati}</td>
                    <td className="px-2 py-1.5 text-right">{r.giorni_lavorativi ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right">{r.rendimento_pct == null ? "—" : r.rendimento_pct.toFixed(2) + "%"}</td>
                    <td className="px-2 py-1.5 text-right">{r.score == null ? "—" : r.score.toFixed(4)}</td>
                    <td className="px-2 py-1.5 text-right">{eu(r.premio_calcolato)}</td>
                    <td className="px-2 py-1.5 text-right">{eu(r.premio_cap)}</td>
                    <td className="px-2 py-1.5 text-right font-medium">{eu(r.premio_assegnato)}</td>
                    <td className="px-2 py-1.5 text-center">{r.premio_is_override ? "Sì" : "—"}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{r.premio_motivazione || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
